import { useEffect } from 'react';

type Options = {
  /** target design height in px for ideal layout (content box excluding header) */
  targetHeight?: number;
  /** target design width in px for ideal layout */
  targetWidth?: number;
  /** min and max scale clamps */
  min?: number;
  max?: number;
  /** optional container selector to measure instead of window */
  container?: HTMLElement | null;
};

/**
 * Computes a CSS var `--section-scale` clamped between min/max so the section content
 * can scale down slightly on short viewports to avoid header overlap.
 */
export function useSmartScale({ targetHeight = 760, targetWidth = 1200, min = 0.82, max = 1, container }: Options = {}) {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const root = document.documentElement;

    const measure = () => {
      const headerH = parseFloat(getComputedStyle(root).getPropertyValue('--header-h')) || 64;
      const availH = (container ? container.clientHeight : window.innerHeight) - headerH;
      const availW = (container ? container.clientWidth : window.innerWidth);
      const scaleH = availH / targetHeight;
      const scaleW = availW / targetWidth;
      const raw = Math.min(scaleH, scaleW);
      const s = Math.max(min, Math.min(max, raw));
      root.style.setProperty('--section-scale', s.toFixed(3));
    };
    measure();

    const onResize = () => measure();
    window.addEventListener('resize', onResize, { passive: true });
    const mo = new MutationObserver(() => measure());
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    return () => {
      window.removeEventListener('resize', onResize);
      mo.disconnect();
    };
  }, [targetHeight, targetWidth, min, max, container]);
}
